import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { validatedInputClassName } from '../ui/formStyles';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  formatNoteDate,
  formatTimeRange,
  getProspectCalendarEntries,
  groupCalendarEntriesByDate,
  isAppointmentEntry,
  todayIsoDate,
} from '../../utils/prospectNotes';

function toIsoDate(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function buildCalendarCells(year, month) {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  for (let i = 0; i < firstWeekday; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(day);
  }
  return cells;
}

function isValidTimeRange(startTime, endTime) {
  if (!startTime || !endTime) return false;
  return endTime > startTime;
}

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function ProspectNotesCalendarModal({ prospect, isOpen, onClose, onAddNote, onAddAppointment }) {
  const { t, formatMessage, locale } = useLanguage();
  const p = t.vendor?.prospects || {};

  const today = todayIsoDate();
  const [todayYear, todayMonth] = today.split('-').map(Number);
  const [viewYear, setViewYear] = useState(todayYear);
  const [viewMonth, setViewMonth] = useState(todayMonth - 1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [appointmentNote, setAppointmentNote] = useState('');
  const [addingAppointment, setAddingAppointment] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedDate(null);
      setViewYear(todayYear);
      setViewMonth(todayMonth - 1);
      setNewNote('');
      setStartTime('09:00');
      setEndTime('10:00');
      setAppointmentNote('');
    }
  }, [isOpen, todayYear, todayMonth]);

  useEffect(() => {
    setNewNote('');
    setStartTime('09:00');
    setEndTime('10:00');
    setAppointmentNote('');
  }, [selectedDate]);

  const entriesByDate = useMemo(() => {
    if (!prospect) return {};
    return groupCalendarEntriesByDate(getProspectCalendarEntries(prospect));
  }, [prospect]);

  const calendarCells = useMemo(
    () => buildCalendarCells(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });

  const weekdayLabels = WEEKDAY_KEYS.map((key) => p.calendarWeekdays?.[key] || key.toUpperCase());

  const inputClass = validatedInputClassName;

  if (!prospect) return null;

  function goPrevMonth() {
    setSelectedDate(null);
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  }

  function goNextMonth() {
    setSelectedDate(null);
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  }

  const selectedEntries = selectedDate ? entriesByDate[selectedDate] || [] : [];
  const selectedNotes = selectedEntries.filter((entry) => !isAppointmentEntry(entry));
  const selectedAppointments = selectedEntries
    .filter((entry) => isAppointmentEntry(entry))
    .sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));

  const timeRangeValid = isValidTimeRange(startTime, endTime);

  async function handleAddNote(e) {
    e.preventDefault();
    const trimmed = newNote.trim();
    if (!trimmed || !selectedDate || !onAddNote) return;
    setAddingNote(true);
    try {
      await onAddNote(prospect.id, trimmed, selectedDate);
      setNewNote('');
    } finally {
      setAddingNote(false);
    }
  }

  async function handleAddAppointment(e) {
    e.preventDefault();
    if (!selectedDate || !onAddAppointment || !timeRangeValid) return;
    setAddingAppointment(true);
    try {
      await onAddAppointment(prospect.id, {
        appointmentDate: selectedDate,
        startTime,
        endTime,
        note: appointmentNote.trim(),
      });
      setAppointmentNote('');
    } finally {
      setAddingAppointment(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={p.notesCalendarTitle}
      size="xl"
    >
      <div className="min-w-0 space-y-4">
        <p className="text-sm text-secondary-600 ">
          {formatMessage(p.notesCalendarSubtitle, { name: prospect.name })}
        </p>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-stretch">
          <div className="min-w-0 rounded-2xl border border-secondary-200/70 bg-white/80 p-3 sm:p-4  ">
            <div className="mb-4 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={goPrevMonth}
                className="touch-target inline-flex items-center justify-center rounded-xl text-secondary-500 transition-colors hover:bg-secondary-100 hover:text-orange-600  "
                aria-label={p.calendarPrevMonth}
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <h4 className="text-sm font-semibold capitalize text-secondary-900 ">
                {monthLabel}
              </h4>
              <button
                type="button"
                onClick={goNextMonth}
                className="touch-target inline-flex items-center justify-center rounded-xl text-secondary-500 transition-colors hover:bg-secondary-100 hover:text-orange-600  "
                aria-label={p.calendarNextMonth}
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5 text-center sm:gap-1">
              {weekdayLabels.map((label) => (
                <div
                  key={label}
                  className="py-1 text-[10px] font-semibold uppercase tracking-wide text-secondary-400 "
                >
                  {label}
                </div>
              ))}
              {calendarCells.map((day, index) => {
                if (!day) {
                  return <div key={`empty-${index}`} className="aspect-square" />;
                }

                const iso = toIsoDate(viewYear, viewMonth, day);
                const dayEntries = entriesByDate[iso] || [];
                const hasNotes = dayEntries.some((entry) => !isAppointmentEntry(entry));
                const hasAppointments = dayEntries.some((entry) => isAppointmentEntry(entry));
                const hasActivity = hasNotes || hasAppointments;
                const isToday = iso === today;
                const isSelected = iso === selectedDate;

                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setSelectedDate(iso)}
                    className={`relative flex aspect-square flex-col items-center justify-center rounded-xl text-sm font-medium transition-colors ${
                      isSelected
                        ? 'bg-orange-500 text-white shadow-md'
                        : isToday
                          ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-300   '
                          : hasActivity
                            ? 'bg-secondary-50 text-secondary-900 hover:bg-orange-50   '
                            : 'text-secondary-700 hover:bg-secondary-100  '
                    }`}
                    aria-label={formatNoteDate(iso, locale)}
                    aria-pressed={isSelected}
                  >
                    {day}
                    <span className="mt-0.5 flex h-1.5 items-center gap-0.5">
                      {hasAppointments ? (
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            isSelected ? 'bg-cyan-200' : 'bg-cyan-500 '
                          }`}
                        />
                      ) : null}
                      {hasNotes ? (
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            isSelected ? 'bg-white/90' : 'bg-orange-500 '
                          }`}
                        />
                      ) : null}
                      {!hasActivity ? <span className="h-1.5 w-1.5" /> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex min-h-[240px] flex-col rounded-2xl border border-secondary-200/70 bg-secondary-50/50 p-3 sm:min-h-[320px] sm:p-4   lg:min-h-0">
            {!selectedDate ? (
              <div className="flex flex-1 items-center justify-center">
                <p className="text-center text-sm text-secondary-500 ">
                  {p.notesCalendarPickDay}
                </p>
              </div>
            ) : (
              <>
                <h4 className="shrink-0 text-sm font-semibold text-secondary-900 ">
                  {formatMessage(p.notesCalendarDayTitle, {
                    date: formatNoteDate(selectedDate, locale),
                  })}
                </h4>

                <div className="mt-3 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-cyan-700 ">
                      {p.calendarAppointmentsTitle}
                    </h5>
                    {selectedAppointments.length === 0 ? (
                      <p className="mt-2 text-sm text-secondary-500 ">
                        {p.calendarNoAppointmentsOnDay}
                      </p>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {selectedAppointments.map((entry, index) => (
                          <div
                            key={entry.id || index}
                            className="rounded-xl border border-cyan-200/70 bg-cyan-50/70 p-3  "
                          >
                            <div className="flex items-center gap-2 text-sm font-medium text-cyan-900 ">
                              <ClockIcon className="h-4 w-4 shrink-0" />
                              <span>
                                {formatTimeRange(entry.start_time, entry.end_time, locale)}
                              </span>
                            </div>
                            {entry.text ? (
                              <p className="mt-2 whitespace-pre-wrap text-sm text-secondary-700 ">
                                {entry.text}
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-secondary-600 ">
                      {p.calendarNotesTitle}
                    </h5>
                    {selectedNotes.length === 0 ? (
                      <p className="mt-2 text-sm text-secondary-500 ">
                        {formatMessage(p.notesCalendarNoNotesOnDay, {
                          date: formatNoteDate(selectedDate, locale),
                        })}
                      </p>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {selectedNotes.map((entry, index) => (
                          <div
                            key={entry.id || index}
                            className="rounded-xl border border-secondary-200/70 bg-white/90 p-3  "
                          >
                            <p className="whitespace-pre-wrap text-sm text-secondary-800 ">
                              {entry.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <form
                  onSubmit={(e) => void handleAddAppointment(e)}
                  className="mt-4 shrink-0 border-t border-secondary-200/60 pt-4 "
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700 ">
                    {p.calendarScheduleAppointment}
                  </p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-xs font-medium text-secondary-700 ">
                        {p.calendarStartTime}
                      </span>
                      <input
                        type="time"
                        className={inputClass}
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        required
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-secondary-700 ">
                        {p.calendarEndTime}
                      </span>
                      <input
                        type="time"
                        className={inputClass}
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        required
                      />
                    </label>
                  </div>
                  {!timeRangeValid ? (
                    <p className="mt-1 text-xs text-red-600 " role="alert">
                      {p.calendarInvalidTimeRange}
                    </p>
                  ) : null}
                  <label className="mt-3 block">
                    <span className="text-xs font-medium text-secondary-700 ">
                      {p.calendarAppointmentNoteLabel}
                    </span>
                    <textarea
                      className={`${inputClass} min-h-[64px] resize-y`}
                      placeholder={p.calendarAppointmentNotePlaceholder}
                      value={appointmentNote}
                      onChange={(e) => setAppointmentNote(e.target.value)}
                    />
                  </label>
                  <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button
                      type="submit"
                      size="small"
                      className="w-full sm:w-auto"
                      loading={addingAppointment}
                      disabled={!timeRangeValid || addingAppointment}
                    >
                      {p.calendarScheduleAppointmentButton}
                    </Button>
                  </div>
                </form>

                <form
                  onSubmit={(e) => void handleAddNote(e)}
                  className="mt-4 shrink-0 border-t border-secondary-200/60 pt-4 "
                >
                  <label className="block">
                    <span className="text-xs font-medium text-secondary-700 ">
                      {p.calendarAddNoteLabel}
                    </span>
                    <textarea
                      className={`${inputClass} min-h-[80px] resize-y`}
                      placeholder={p.calendarAddNotePlaceholder}
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                    />
                  </label>
                  <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button
                      type="submit"
                      size="small"
                      className="w-full sm:w-auto"
                      loading={addingNote}
                      disabled={!newNote.trim() || addingNote}
                    >
                      {p.addNote}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-secondary-200/60 pt-4 sm:flex-row sm:justify-end ">
          <Button variant="secondary" className="w-full sm:w-auto" onClick={onClose}>
            {p.close || t.common.close}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ProspectNotesCalendarModal;
